import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { cx } from '@libs/classnames';
import { Form } from '@ui/components/Form';
import { GoldenModal } from '@ui/components/GoldenModal/GoldenModal';
import { ThemeButton } from '@ui/components/ThemeButton';

import styles from './ClubAdminForm.module.css';
import {
  DescriptionInput,
  EntranceInput,
  TelegramInput,
  ThemeColorInput,
  TitleInput,
  AdminInput,
} from '@features/club/ui/ClubFormInput';
import { ClubAdminDto } from '@features/shared/api/typings';

export type UpdateClubFormValues = {
  title: string;
  description: string;
  telegramChannel: string;
  administrator: ClubAdminDto;
  themeColor: string | null;
  entrance?: 'Open' | 'Moderated';
};

export type Props = {
  className?: string;
  club: UpdateClubFormValues;
  onSubmit: (newClub: UpdateClubFormValues) => Promise<UpdateClubFormValues>;
  disabled?: boolean;
};

export function ClubAdminForm({
  className,
  club,
  onSubmit,
  disabled,
}: Props) {
  const [successModal, setSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<UpdateClubFormValues>({
    disabled: disabled || isSubmitting,
    defaultValues: club,
  });

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(methods.getValues());
      setSuccessModal(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, methods]);

  const onCancel = useCallback(() => {
    methods.reset();
  }, [methods]);

  useEffect(() => {
    if (successModal) {
      setTimeout(() => setSuccessModal(false), 3000);
    }
  }, [successModal]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit}>
        <Form className={cx(className, styles.root)}>
          <TitleInput invisible />
          <DescriptionInput />
          <AdminInput />
          <TelegramInput />
          <ThemeColorInput />
          <EntranceInput />

          <div className={styles.buttonBar}>
            <ThemeButton inverted onClick={onCancel}>
              Отмена
            </ThemeButton>
            <ThemeButton
              onClick={handleSubmit}
              disabled={!methods.formState.isValid || disabled || isSubmitting}
            >
              Сохранить
            </ThemeButton>

            {successModal && (
              <GoldenModal className={styles.modal}>Данные клуба успешно сохранены</GoldenModal>
            )}
          </div>
        </Form>
      </form>
    </FormProvider>
  );
}
